import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { animated, useTransition } from "@react-spring/web";
import { toast } from "react-toastify";
import { useInView } from "react-intersection-observer";

import Actions from "src/components/actionbar";
import { TabBar } from "src/components/tabbar";
import { ExpandableToggle } from "../components/expandableToggle";
import Reveal from "src/components/animation";
import Button from "src/components/button";
import Toast from "src/components/toast";
import { AutoGrowInput } from "src/components/input";
import Slider from "src/components/slider";
import Text from "src/components/text";

import { useCreateUserRole, useDeleteUserRole, useFetchUserRole, useFetchUserRolesForAccount, useUpdateUserRole } from "src/query/settings/userRoles";
import { useQueryClient } from "@tanstack/react-query";
import { hideSideMenu } from "src/redux/actions/hub";

import { isRoleDisabled, roleConfiguration } from "src/utils/userRoles.helper";

import _ from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";

interface ToggleSectionSkeletonProps {
  section: {
    options: { key: string }[];
  };
}

interface RoleSectionProps {
  section: any;
  detailsLoading: boolean;
  localRoleDetails: any;
  isDisabled: boolean;
  handleToggleChange: (sectionKey: string, optionKey: string, newValue: boolean) => void;
  setActiveSection: (key: string) => void;
  isProgrammaticScroll: boolean;
}

interface DeleteUserRoleProps {
  show: boolean;
  onClose: () => void;
  item: { id: string; name: string } | null;
  onConfirm: (id: string) => void;
  isDeleting?: boolean;
}

const ManageUserRoles: React.FC = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeRoleIndex, setActiveRoleIndex] = useState(0);
  const [activeSection, setActiveSection] = useState<string | null>(roleConfiguration[0].key);
  const [currentRolePermissions, setCurrentRolePermissions] = useState<any>({});
  const [activeRoleId, setActiveRoleId] = useState<string | null>(null);
  const [showDeleteSlider, setShowDeleteSlider] = useState(false);
  const [roles, setRoles] = useState<any[]>([]);
  const [previousRole, setPreviousRole] = useState<any>(null);

  const { data: userRoles = [], refetch } = useFetchUserRolesForAccount();

  //activeRoleId
  const editingRole = roles.find(r => r.id === activeRoleId);

  // Store the original role details to compare changes
  const persistedCurrentRole = useRef<any>({});

  const scrollFlagRef = useRef(false);

  const activeSectionRef = useRef<string | null>(activeSection);

  const { id } = useParams<{ id: string }>();

  const isNewRole = id === "new";

  const isDisabled = isRoleDisabled(activeRoleId, persistedCurrentRole?.current.name, persistedCurrentRole?.current.description);

  const { data: roleDetails, isLoading: detailsLoading } = useFetchUserRole(id);
  const deleteUserRole = useDeleteUserRole();
  const updateUserRole = useUpdateUserRole();
  const createUserRole = useCreateUserRole();

  // Determine if there are unsaved changes
  const nameChanged = !detailsLoading && editingRole?.name !== persistedCurrentRole.current.name;
  const permissionsChanged =
    !detailsLoading &&
    !_.isEqual(currentRolePermissions, persistedCurrentRole.current);

  const isUpdatingRole = nameChanged || permissionsChanged;

  const handleBackClick = () => {
    dispatch(hideSideMenu(false));
    navigate("/settings/user-management/user");
  };

  // Handle delete role click
  const handleDeleteClick = () => {
    if (!persistedCurrentRole?.current.name) return;
    setShowDeleteSlider(true);
  };

  const handleConfirmDelete = () => {
    if (!persistedCurrentRole?.current.name) return;
    deleteUserRole.mutate(persistedCurrentRole?.current.id);
  };

  const isMutationPending = updateUserRole.isPending || createUserRole.isPending;

  const addButtonTransitions = useTransition(!isUpdatingRole, {
    initial: { opacity: 0, transform: "translateY(0px)" },
    from: { opacity: 0, transform: "translateY(-10px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-10px)" },
  });

  // Handle toggle change for permissions
  const handleToggleChange = (
    sectionKey: string,
    optionKey: string,
    newValue: boolean
  ) => {
    setCurrentRolePermissions((prev) => ({
      ...prev,
      [sectionKey]: {
        ...prev[sectionKey],
        [optionKey]: newValue,
      },
    }));
  };

  // Handle save role
  const handleSaveRole = () => {
    if (!currentRolePermissions) return;

    const currentRole = roles.find(r => r.id === activeRoleId);
    const currentName = currentRole?.name?.trim() || "";

    if (!currentName) {
      toast(
        <Toast.warning
          heading="Warning"
          message="Enter a valid role name."
        />
      );
      return;
    }
  
    const payload = {
      ...currentRolePermissions,
      name: currentName,
    };

    if (isNewRole) {
      createUserRole.mutate(payload);
    } else {
      updateUserRole.mutate({ id: activeRoleId!, data: payload });
    }
  };

  // Handle create new role
  const handleCreateNewRole = () => {
    const active = roles[activeRoleIndex] ?? null;
    setPreviousRole(active); 
    navigate("/settings/user-roles/new");

    const defaultPermissions = Object.fromEntries(
      roleConfiguration.map(section => [
        section.key,
        Object.fromEntries(section.options.map(option => [option.key, false])),
      ])
    );

    const newRole = {
      id: "new-role-temp-id",
      name: "Untitled",
      permissions: defaultPermissions,
    };

    setRoles((prev) => [newRole, ...prev]);
    setActiveRoleIndex(0);
    setCurrentRolePermissions(defaultPermissions);
    setActiveRoleId("new-role-temp-id");
  };

  // Handle cancel action
  const handleCancel = () => {
    if (isNewRole) {
      setRoles(prev => prev.filter(r => r.id !== "new-role-temp-id"));
  
      if (previousRole) {
        setActiveRoleId(previousRole.id);
        setCurrentRolePermissions(previousRole);
  
        const index = userRoles.findIndex(r => r.id === previousRole.id);
        setActiveRoleIndex(index >= 0 ? index : 0);
  
        navigate(`/settings/user-roles/${previousRole.id}`);
      } else if (userRoles.length > 0) {
        const first = userRoles[0];
        setActiveRoleId(first.id);
        setCurrentRolePermissions(first.permissions);
        setActiveRoleIndex(0);
  
        navigate(`/settings/user-roles/${first.id}`);
      }
    } else {
      const currentRole = roles.find(r => r.id === id);
      if (currentRole) {
        setCurrentRolePermissions(persistedCurrentRole.current);
        setRoles(prev =>
          prev.map(r =>
            r.id === id ? { ...r, name: persistedCurrentRole.current.name } : r
          )
        );
      }
    }
  };
  

// Scroll to section
const scrollToPage = (id: string | number) => {
  const sectionId = `section_${id}`;
  const sectionElement = document.getElementById(sectionId);
  const rightPanel = document.querySelector(`.${styles.right}`) as HTMLElement;

  if (sectionElement && rightPanel) {
    const offset = sectionElement.offsetTop - rightPanel.offsetTop;
    const extraOffset = 20;
    const headerHeight = 56;
    setActiveSection(id.toString());

    scrollFlagRef.current = true;
    
    rightPanel.scrollTo({
      top: offset - headerHeight + extraOffset,
      behavior: "smooth",
    });

    setTimeout(() => {
      scrollFlagRef.current = false;
    }, 800);
  }
};

const handleScroll = (rightPanel: HTMLElement) => {
  if (scrollFlagRef.current) return;

  requestAnimationFrame(() => {
    const scrollTop = rightPanel.scrollTop;
    const viewHeight = rightPanel.clientHeight;
    const visibilityThreshold = 0.5;

    let mostVisibleSection: string | null = null;
    let maxVisibleRatio = 0;

    for (const section of roleConfiguration) {
      const el = document.getElementById(`section_${section.key}`);
      if (!el) continue;

      const elTop = el.offsetTop - rightPanel.offsetTop;
      const elBottom = elTop + el.offsetHeight;

      const visibleTop = Math.max(elTop, scrollTop);
      const visibleBottom = Math.min(elBottom, scrollTop + viewHeight);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);
      const visibleRatio = visibleHeight / el.offsetHeight;

      if (visibleRatio > maxVisibleRatio) {
        maxVisibleRatio = visibleRatio;
        mostVisibleSection = section.key;
      }
    }

    if (
      mostVisibleSection &&
      maxVisibleRatio > visibilityThreshold &&
      activeSectionRef.current !== mostVisibleSection
    ) {
      activeSectionRef.current = mostVisibleSection;
      setActiveSection(mostVisibleSection);

      const activeNavItem = document.getElementById(`nav_${mostVisibleSection}`);
      if (activeNavItem) {
        scrollFlagRef.current = true;
        activeNavItem.scrollIntoView({ block: "nearest", behavior: "smooth"});

        setTimeout(() => {
          scrollFlagRef.current = false;
        }, 150);
      }
    }
  });
};

  useEffect(() => {
    dispatch(hideSideMenu(true));
  
    return () => {
      dispatch(hideSideMenu(false));
    };
  }, []);

  // Load user roles and set initial state
  useEffect(() => {
    if (!userRoles || _.size(userRoles) === 0) return;

    setRoles((prev) => {
      const temp = prev.find(r => r.id === "new-role-temp-id");
      const apiRoles = userRoles.map(r => ({ ...r }));
      return temp ? [temp, ...apiRoles] : apiRoles;
    });

    if (!id) {
      const first = userRoles[0];
      navigate(`/settings/user-roles/${first.id}`, { replace: true });
    }
  }, [userRoles, id, navigate]);

  // Load role details when id or roles change
  useEffect(() => {
    if (roles.length === 0) return;

    const firstSectionKey = roleConfiguration[0]?.key; 

    if (isNewRole) {
      const index = roles.findIndex(r => r.id === "new-role-temp-id");
      setActiveRoleIndex(index >= 0 ? index : 0);

      if (!currentRolePermissions || _.isEmpty(currentRolePermissions)) {
        const defaultPermissions = Object.fromEntries(
          roleConfiguration.map(section => [
            section.key,
            Object.fromEntries(section.options.map(option => [option.key, false]))
          ])
        );
        setCurrentRolePermissions(defaultPermissions);
        setActiveRoleId("new-role-temp-id");
      }

      if (firstSectionKey) {
        setActiveSection(firstSectionKey);
        scrollToPage(firstSectionKey);
      }

    } else if (!detailsLoading && roleDetails && activeRoleId !== id) {
      persistedCurrentRole.current = roleDetails;
      setCurrentRolePermissions(roleDetails);
      setActiveRoleId(id!);

      const index = roles.findIndex(r => r.id === id);
      setActiveRoleIndex(index >= 0 ? index : 0);

      if (firstSectionKey) {
        setActiveSection(firstSectionKey);
        scrollToPage(firstSectionKey);
      }
    }

  }, [roles, isNewRole, detailsLoading, roleDetails, activeRoleId, id]);

  // Handle success and error notifications for delete, create, and update operations
  useEffect(() => {

    if (deleteUserRole.isSuccess && persistedCurrentRole?.current) {
      refetch().then((res) => {
        const updatedRoles = res.data;
        const firstUserRole = updatedRoles?.[0];
        if (firstUserRole) {
          navigate(`/settings/user-roles/${firstUserRole.id}`);
        }
      });

      toast(
        <Toast.Sucess
          heading="Role deleted"
          message={
            <>
              The role{" "}
              <Text
                text={`"${persistedCurrentRole?.current.name ?? ""}"`}
                tooltip={{ placement: "auto" }}
                className={styles.roleName}
              />{" "}
              has been deleted successfully.
            </>
          }
        />
        );
      
      setShowDeleteSlider(false);
    }

    if (deleteUserRole.isError && persistedCurrentRole?.current) {
      toast(
        <Toast.Failure
          heading="Failed to delete role"
          message={
            <>
              Something went wrong while deleting{" "}
                <Text
                 text={`"${persistedCurrentRole?.current.name ?? ""}"`}
                 tooltip={{ placement: "auto" }}
                 className={styles.roleName}
                />
              .
            </>
          }
        />
      );
      
    }
  }, [deleteUserRole.isSuccess, deleteUserRole.isError]);

  useEffect(() => {
    if (createUserRole.isSuccess) {
      const newRole = createUserRole.data;
      const currentRole = roles.find(r => r.id === activeRoleId);
      const currentName = currentRole?.name ?? "Untitled";
      const perms = newRole?.permissions ?? currentRolePermissions ?? {};

      const syncedData = {
        ...perms,
        id: newRole.id,
        name: newRole.name ?? "Untitled",
      };
      persistedCurrentRole.current = syncedData;
      setCurrentRolePermissions(syncedData);

      const filtered = roles.filter(r => r.id !== "new-role-temp-id");
      const updated = [...filtered, syncedData];
      const newIndex = updated.findIndex(r => r.id === newRole.id);

  
      setRoles(updated);
      setActiveRoleIndex(newIndex);
      setActiveRoleId(newRole.id);
      setCurrentRolePermissions(syncedData);
  
      navigate(`/settings/user-roles/${newRole.id}`);
  

      const firstSectionKey = roleConfiguration[0]?.key;
      if (firstSectionKey) {
        setActiveSection(firstSectionKey);
        scrollToPage(firstSectionKey);
      }

      toast(
        <Toast.Sucess
          heading="Role created"
          message={
            <>
              The role{" "}
                <Text
                  text={`"${currentName}"`}
                  tooltip={{ placement: "auto" }}
                  className={styles.roleName}
                />
              {" "}
              has been created successfully.
            </>
          }
        />
      );
    }

    if (createUserRole.isError) {
      const currentRole = roles.find(r => r.id === activeRoleId);
      const currentName = currentRole?.name ?? "Untitled";
      toast(
        <Toast.Failure
          heading="Failed to create role"
          message={
            <>
              Something went wrong while creating the role{" "}
                <Text
                  text={`"${currentName}"`}
                  tooltip={{ placement: "auto" }}
                  className={styles.roleName}
                />
              . Please try again.
            </>
          }
        />
      );
    }
  }, [createUserRole.isSuccess, createUserRole.isError]);

  useEffect(() => {
    if (updateUserRole.isSuccess) {
      const currentRole = roles.find(r => r.id === activeRoleId);
      const currentName = currentRole?.name ?? "Untitled";

      const syncedData = { ...currentRolePermissions, name: currentName };
      persistedCurrentRole.current = syncedData;
      setCurrentRolePermissions(syncedData);

      setCurrentRolePermissions({ ...persistedCurrentRole.current });

      setRoles(prev =>
        prev.map(r =>
          r.id === activeRoleId ? { ...r, ...persistedCurrentRole.current } : r
        )
      );

      toast(
        <Toast.Sucess
          heading="Role updated"
          message={
            <>
              The role{" "}
                <Text
                  text={`"${currentName}"`}
                  tooltip={{ placement: "auto" }}
                  className={styles.roleName}
                />
              {" "}
              has been updated successfully.
            </>
          }
        />
      );
    }

    if (updateUserRole.isError) {
      const currentRole = roles.find(r => r.id === activeRoleId);
      const currentName = currentRole?.name ?? "Untitled";

      toast(
        <Toast.Failure
          heading="Failed to update role"
          message={
            <>
              Something went wrong while updating the role{" "}
                <Text
                  text={`"${currentName}"`}
                  tooltip={{ placement: "auto" }}
                  className={styles.roleName}
                />
              . Please try again.
            </>
          }
        />
      );
    }
  }, [updateUserRole.isSuccess, updateUserRole.isError]);

  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  useEffect(() => {
    const rightPanel = document.querySelector(`.${styles.right}`) as HTMLElement;
    if (!rightPanel) return;
  
    const scrollListener = () => handleScroll(rightPanel);
  
    rightPanel.addEventListener("scroll", scrollListener);
    return () => rightPanel.removeEventListener("scroll", scrollListener);
  }, []);
  

  return (
    <div className={styles.container}>
      <div className={styles.heading}>
        <div className={styles.header}>
          <i className="paperflite-left-arrow" onClick={handleBackClick} />
          <div className={styles.title}>manage roles</div>
        </div>
        <div className={styles.subtitle}>
          {addButtonTransitions((style, isVisible) =>
            isVisible && !isUpdatingRole && (
              <animated.div style={{ position: "absolute", right: 0, ...style }} className={styles.actionButton}>
                <Actions.Button
                  width={3}
                  icon="paperflite-add"
                  title="create new role"
                  onClick={handleCreateNewRole}
                />
                {!isDisabled && persistedCurrentRole?.current && persistedCurrentRole.current.id !== "new-role-temp-id" && (
                  <Actions.IconButton
                    icon="paperflite-delete"
                    tooltip="Delete this role"
                    onClick={handleDeleteClick}
                  />
                )}
              </animated.div>
            )
          )}

          <Reveal show={isUpdatingRole && !detailsLoading}>
            <div className={styles.actions}>
              <div className={styles.actionsInfo}>click here to</div>
              <Button
                variant="primary"
                disabled={isMutationPending}
                text={isMutationPending ? "Saving..." : "Save"}
                icon="paperflite-tick"
                onClick={() => {
                  if (!activeRoleId) return;
                  handleSaveRole();
                }}
              />
              <Button
                variant="secondary"
                text="Cancel"
                onClick={handleCancel}
              />
            </div>
          </Reveal>
        </div>
      </div>
      <div className={styles.tabSection}>
        <TabBar
          className={cx(styles.tabs, isUpdatingRole && styles.tabBarDisabled)}
          active={roles.findIndex(r => r.id === activeRoleId)}
          scrollIntoView={true}
          onSelectionChange={(index) => {
            if (isUpdatingRole) return;
            setActiveRoleIndex(index);
            const role = roles[index];
            if (role.id === "new-role-temp-id") {
              navigate("/settings/user-roles/new");
            } else {
              navigate(`/settings/user-roles/${role.id}`);
            }
            if (roleConfiguration.length > 0) {
              const firstSection = roleConfiguration[0].key;
              setActiveSection(firstSection);
              scrollToPage(firstSection);
            }
          }}
          tabSize="auto"
          gap={32}
        >
          {roles.map((role: any, index) => {
            const nonEditableRoles = [
              "Administrator",
              "Normal User",
              "BI Analyst",
              "Content Publisher",
            ];

            const roleIsEditable =
            (role.description == null || role.id === "new-role-temp-id") &&
            !nonEditableRoles.includes(role.name);
            const isActive = role.id === activeRoleId;

            return (
              <span key={role.id} className={cx(
                styles.tabItem,
                isActive && styles.tabBarActive,
                isUpdatingRole && !isActive && styles.tabDisabled,
                isNewRole ? styles.slideIn : "", 
                role.removed ? styles.slideOut : ""
              )}>
                {roleIsEditable && (
                  <div
                  className={cx(
                    styles.tabInputContainer,
                    isActive && styles.tabInputActive,
                    !isActive && isUpdatingRole && styles.tabInputDisabled
                  )}
                >
                    <AutoGrowInput
                      value={role.name}
                      onChange={(e) => {
                        if (role.id !== activeRoleId) return; 
                        const newName = e.target.value;
                        setRoles((prev) =>
                          prev.map((r) => (r.id === role.id ? { ...r, name: newName } : r))
                        );
                      }}
                      className={styles.tabInput}
                    />
                  </div>
                )}
                {!roleIsEditable && role.name}
              </span>
            );
          })}
        </TabBar>
      </div>
      <div className={styles.body}>
        <div className={styles.left}>
          <div className={styles.info}>
            Here's a list of modules you can control perimissions
          </div>
          <div className={styles.panel}>
            {roleConfiguration.map((section) => (
              <div
                id={`nav_${section.key}`} 
                key={section.key}
                data-section={section.key} 
                className={cx(styles.navItem, {
                  [styles.active]: activeSection === section.key,
                })}
                onClick={() => scrollToPage(section.key)}
              >
                <i className={cx("paperflite-icon", section.icon)}></i>
                <span>{section.title}</span>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.right}>
          {detailsLoading && roleConfiguration.map((section) => (
            <ToggleSectionSkeleton key={section.key} section={section} />
          ))}
          {roleConfiguration.map((section) => (
            <RoleSection
              key={section.key}
              section={section}
              detailsLoading={detailsLoading}
              localRoleDetails={currentRolePermissions}
              isDisabled={isDisabled}
              handleToggleChange={handleToggleChange}
              setActiveSection={setActiveSection}
              isProgrammaticScroll={scrollFlagRef.current}
            />
          ))}
        </div>
      </div>
      <DeleteUserRole
        show={showDeleteSlider}
        onClose={() => setShowDeleteSlider(false)}
        item={persistedCurrentRole.current ? { id: persistedCurrentRole.current.id, name: persistedCurrentRole.current.name } : null}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteUserRole.isPending}
      />
    </div>
  );
}

export default ManageUserRoles;

const ToggleSectionSkeleton: React.FC<ToggleSectionSkeletonProps> = ({ section }) => {
  return (
    <div className={cx(styles.sectionWrapper, styles.visible, styles.toggleSkeleton)}>
      <div className={styles.skeletonTitle}></div>
      <div className={styles.toggleGroup}>
        {section.options.map((option) => (
          <div key={option.key} className={styles.optionContainer}>
            <div className={styles.subText}>
              <div className={styles.optionLabel}></div>
              <div className={styles.optionDescription}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const RoleSection: React.FC<RoleSectionProps> = ({
  section,
  detailsLoading,
  localRoleDetails,
  isDisabled,
  handleToggleChange,
  setActiveSection,
  isProgrammaticScroll
}) => {
  const { ref, inView } = useInView({
    threshold: 0.5,
    root: document.querySelector(".right"),
  });

  useEffect(() => {
    if (inView && !isProgrammaticScroll) {
      setActiveSection(section.key);
    }
  }, [inView, section.key, setActiveSection]);

  return (
    <div
      ref={ref}
      className={cx(styles.sectionWrapper, {
        [styles.visible]: !detailsLoading,
      })}
      id={`section_${section.key}`}
    >
      <div className={styles.switchTitle}>{section.title}</div>
      <p className={styles.sectionDescription}>{section.description}</p>

      <div className={styles.toggleGroup}>
        {section.options.map((option: any) => (
          <ExpandableToggle
            key={option.key}
            id={`${section.key}-${option.key}`}
            title={option.label}
            description={option.description}
            value={localRoleDetails?.[section.key]?.[option.key] ?? false}
            onChange={(newValue) => {
              if (isDisabled) return;
              handleToggleChange(section.key, option.key, newValue);
            }}
            collapsible={false}
            disabled={isDisabled}
          />
        ))}
      </div>
    </div>
  );
};

const DeleteUserRole: React.FC<DeleteUserRoleProps> = ({
  show,
  onClose,
  item,
  onConfirm,
  isDeleting = false,
}) => {
  if (!item) return null;

  const handleConfirm = () => {
    onConfirm(item.id);
  };

  return (
    <Slider.Dialog
      width="380"
      show={show}
      onClose={onClose}
      className={styles.sliderContainer}
    >
      <Slider.Header
        title="deleting"
        message={`role: ${item?.name ?? ""}`}
      />
      <Slider.Body>
        <div className={styles.body}>
          <div className={styles.description}>
            Are you sure you want to delete
            <strong> “{item?.name || "this role"}” </strong>? <br />
            This action cannot be undone.
          </div>
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          variant="primary"
          onClick={handleConfirm}
          text={isDeleting ? "Deleting..." : "Confirm"}
          disabled={isDeleting}
        />
        <Button onClick={onClose} text="Cancel" />
      </Slider.Actions>
    </Slider.Dialog>
  );
};